import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import { postFormatDate } from '../../common/util';
import DataAid from '../../common/constants/data-aids';
import VolumePopup from '../../common/components/VolumePopup';
import useAudioPlayer from '../../common/hooks/useAudioPlayer';
import useAccordion from '../../common/hooks/useAccordion';

const generateStyles = () => ({
  card: {
    boxShadow: '0px 30px 30px rgba(0, 0, 0, 0.05)',
    borderRadius: '5px',
    padding: 'medium',
    margin: '0 0 small',
    borderColor: '#f6f6f6',
    textAlign: 'center',
    ['@md']: {
      textAlign: 'left'
    }
  },
  linkContainer: {
    color: 'neutral',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    ['@md']: {
      width: '75%'
    }
  },
  link: {
    color: 'primary',
    fontSize: 11,
    marginBottom: 'xsmall',
    display: 'inline-block',
    ['@md']: {
      fontSize: 12
    }
  },
  title: {
    marginBottom: 'xxsmall',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '25px'
  },
  date: {
    marginVertical: 'xsmall',
    fontSize: 12
  },
  playContainer: {
    textAlign: 'center',
    paddingVertical: 'xsmall'
  },
  toggleContainer: {
    alignItems: 'center'
  },
  progress: {
    paddingVertical: 'xsmall'
  },
  audioPlayer: {
    alignItems: 'center',
    paddingVertical: 'small'
  },
  desktopVolumeContainer: {
    display: 'none',
    ['@md']: {
      display: 'block'
    }
  },
  mobileVolumeContainer: {
    display: 'block',
    ['@md']: {
      display: 'none'
    }
  }
});

function Card(props) {
  const {
    episode: { title, description, published, guid, enclosure },
    podcastTitle,
    setPlayerIndex,
    isPlaying,
    link,
    index,
    staticContent,
    locale,
    isPrimaryLight
  } = props;

  if (!enclosure) {
    return null;
  }

  const { Accordion, AccordionButton } = useAccordion(description, staticContent);
  const { playButton, progress, reset, volume, mute } = useAudioPlayer(
    enclosure,
    isPrimaryLight,
    index,
    setPlayerIndex
  );
  const styles = generateStyles(isPlaying);
  useEffect(() => {
    if (!isPlaying) {
      reset();
    }
  }, [isPlaying]);

  const Grid = UX2.Component.Grid;
  const Cell = Grid.Cell;

  const getTCCLString = UX2.utils.TCCLUtils.getTCCLString;

  return (
    <UX2.Group.Card style={ styles.card } id={ guid } data-aid={ DataAid.PODCAST_CARD_RENDERED }>
      <Grid gutter={ false }>
        <Cell
          xs={ 12 }
          md={ 1.5 }
          style={ styles.playContainer }
          data-aid={ DataAid.PODCAST_PLAYBUTTON_RENDERED }
        >
          { playButton }
        </Cell>
        <Cell xs={ 12 } md={ 10.5 }>
          { podcastTitle && (
            <UX2.Element.Block style={ styles.linkContainer }>
              <UX2.Element.Link
                style={ styles.link }
                target='_blank'
                href={ link }
                data-aid={ DataAid.PODCAST_TITLE_RENDERED }
                data-tccl={ getTCCLString({
                  eid: 'ux2.podcast.podcast_title.click',
                  type: 'click'
                }) }
              >
                { podcastTitle.toUpperCase() }
              </UX2.Element.Link>
            </UX2.Element.Block>
          ) }
          <Grid offset={ false }>
            <Cell md={ 10 }>
              <UX2.Element.Heading
                tag='h3'
                style={ styles.title }
                children={ title }
                data-aid={ DataAid.PODCAST_EPISODE_TITLE_RENDERED }
              />
            </Cell>
            <Cell md={ 2 } style={ styles.desktopVolumeContainer }>
              <VolumePopup mute={ mute } isPlaying={ isPlaying }>
                { volume }
              </VolumePopup>
            </Cell>
          </Grid>
          <UX2.Element.Details.Minor style={ styles.date }>
            { `${staticContent.posted} ${postFormatDate(published, locale)}` }
          </UX2.Element.Details.Minor>
          <Grid style={ styles.audioPlayer }>
            <Cell>
              <UX2.Element.Block
                style={ styles.progress }
                data-aid={ DataAid.PODCAST_PROGRESS_RENDERED }
              >
                { progress }
              </UX2.Element.Block>
            </Cell>
          </Grid>
          <Grid style={ styles.toggleContainer }>
            <Cell xs={ 8 }>
              <AccordionButton />
            </Cell>
            <Cell xs={ 4 } style={ styles.mobileVolumeContainer }>
              <VolumePopup mute={ mute } isPlaying={ isPlaying }>
                { volume }
              </VolumePopup>
            </Cell>
          </Grid>
          <Accordion />
        </Cell>
      </Grid>
    </UX2.Group.Card>
  );
}

Card.propTypes = {
  episode: PropTypes.object.isRequired,
  podcastTitle: PropTypes.string,
  setPlayerIndex: PropTypes.func,
  isPlaying: PropTypes.bool,
  index: PropTypes.number,
  link: PropTypes.string,
  staticContent: PropTypes.object,
  locale: PropTypes.string,
  isPrimaryLight: PropTypes.bool
};

export default Card;
